import Cookies from "js-cookie"
import axios from "axios"
import { createDiscreteApi } from "naive-ui"
import router from "@/router"
import { AuthRouteNameEnum } from "@/shared/enums/routes/auth.enum.ts"
import { UserRouteNameEnum } from "@/shared/enums/routes/user.enum.ts"
import { useAuthStore } from "@/store/authStore"

// Detached message API so the interceptor can surface toasts from outside the
// component tree (App.vue's NMessageProvider isn't reachable here).
const { message: discreteMessage } = createDiscreteApi(["message"])

// Account-lifecycle 403 codes the backend returns from RequireActiveAccount.
// When a write 403s with one of these, the account changed under the user
// (or a stale tab fired) — re-sync status and bounce to the status screen
// instead of failing silently. Debounced so a burst of 403s shows one toast.
const ACCOUNT_BLOCK_CODES = new Set([
	"account.pending_moderation",
	"account.rejected",
	"account.suspended",
	"account.documents_expired",
])
let lastAccountToastAt = 0
const handleAccountBlock = (code: string, title: string) => {
	const now = Date.now()
	if (now - lastAccountToastAt > 3000) {
		lastAccountToastAt = now
		// Text comes straight from the backend (localized errors[0].title).
		if (title) discreteMessage.error(title)
	}
	// Refresh status, then route to the single status screen.
	void useAuthStore().syncFromServer().finally(() => {
		if (router.currentRoute.value.name !== UserRouteNameEnum.accountStatus) {
			void router.push({ name: UserRouteNameEnum.accountStatus })
		}
	})
}


const config: any = {
	baseURL: import.meta.env.VITE_BASE_URL,
	headers: {
		// NOTE: Access-Control-* are RESPONSE headers the server sets — a client
		// must never send them. Doing so makes the browser add them to the
		// preflight's Access-Control-Request-Headers, which the server then
		// rejects ("... is not allowed by Access-Control-Allow-Headers").
		// CORS is configured server-side (config/cors.php); the client only
		// declares what it accepts.
		accept: "application/json",
	}
}

const axiosInstance = axios.create(config)
const axiosInstanceNoAuth = axios.create(config)
let isRefreshing = false
let failedQueue: { resolve: (value: any) => void, reject: (reason?: any) => void }[] = []

const processQueue = (error: any, token = null) => {

	failedQueue.forEach(prom => {
		if (error) {
			prom.reject(error)
		} else {
			prom.resolve(token)
		}
	})

	failedQueue = []
}

axiosInstance.interceptors.request.use((config) => {
		// Skip auth for any request that isn't to our own API. Presigned S3 /
		// MinIO URLs already carry an X-Amz-Signature in the query string;
		// adding an Authorization header makes the storage reject the request
		// with "multiple authentication types".
		const apiBase = (import.meta.env.VITE_BASE_URL as string | undefined) ?? ""
		const isExternal = config.url
			? /^https?:\/\//.test(config.url) && (apiBase === "" || !config.url.startsWith(apiBase))
			: false
		if (isExternal) return config

		let authCookie = null
		if (Cookies.get("auth")) authCookie = JSON.parse(Cookies.get("auth"))
		// else router.push({name: "auth.login"})

		const authToken = authCookie?.token

		if (authToken) {
			config.headers.Authorization = `Bearer ${authToken}`
		}

		// Read straight from localStorage — pulling pinia in here would
		// trigger a circular import (the locale store itself uses axios).
		// Anonymous requests on the auth pages still pass a language hint
		// so server-rendered error messages match the picker choice.
		try {
			const lc = localStorage.getItem("indoorad.locale")
			if (lc) config.headers["Accept-Language"] = lc
		} catch { /* ignore */ }

		return config
	}, (error) => {
		return Promise.reject(error)
	}
)

axiosInstance.interceptors.response.use((response) => response,
	(error) => {
		let errResp = error.response

		let authCookie = null
		if (Cookies.get("auth")) {
			authCookie = JSON.parse(Cookies.get("auth"))
		}

		const refreshToken = authCookie?.refreshToken

		const originalRequest = error.config

		// Auth endpoints' 401 means "bad credentials" / "expired token in the
		// body" — not "the caller's access token expired". Refreshing here
		// would just hide the real error behind a useless second request.
		const url = String(originalRequest?.url ?? "")
		const isAuthEndpoint =
			url.includes("auth/login") ||
			url.includes("auth/refresh") ||
			url.includes("auth/register") ||
			url.includes("auth/forgot-password") ||
			url.includes("auth/reset-password") ||
			url.includes("auth/confirm")

		if (errResp.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
			if (isRefreshing) {
				return new Promise(function(resolve, reject) {
					failedQueue.push({ resolve, reject })
				}).then(token => {
					originalRequest.headers["Authorization"] = "Bearer " + String(token)
					return axiosInstance(originalRequest)
				}).catch(err => {
					return Promise.reject(err)
				})
			}

			originalRequest._retry = true
			isRefreshing = true

			return new Promise(function(resolve, reject) {
				axiosInstanceNoAuth.post("api/v1/auth/refresh", { refresh_token: refreshToken })
					.then(({ data }) => {
						const tokens = data?.data?.attributes
						if (!tokens?.access_token) {
							throw new Error("refresh: missing access_token in response")
						}
						useAuthStore().commitRefresh(tokens.access_token, tokens.refresh_token)
						processQueue(null, tokens.access_token)
						resolve(axiosInstance(originalRequest))
					})
					.catch((err) => {
						Cookies.remove("auth")
						router.push({ name: AuthRouteNameEnum.login })
						processQueue(err, null)
						reject(err)
					})
					.finally(() => {
						isRefreshing = false
					})
			})


		}

		// Account-lifecycle safety net: a write that 403s because the account
		// is pending_moderation / rejected / suspended. The router gate usually
		// keeps these users off write flows, but this catches mid-session status
		// changes and stale tabs so nothing fails silently.
		if (errResp?.status === 403) {
			const code = errResp?.data?.errors?.[0]?.code
			const title = errResp?.data?.errors?.[0]?.title
			if (code && ACCOUNT_BLOCK_CODES.has(code)) {
				handleAccountBlock(code, title)
			}
		}

		return Promise.reject(error)
	}
)

export default axiosInstance
